/**
 * Live Visitors — realtime storefront visitors from PostHog.
 *
 * Calls zaviago_backend.api.live_visitors.get_live_visitors, which resolves
 * the tenant server-side from the session user's company and aggregates all
 * of the company's storefronts. Polls every 30s.
 *
 * Shared by the home dashboard card (DashboardLiveVisitors) and the
 * Business Dashboard widget (LiveVisitorsWidget).
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { frappeMethod } from '@/lib/erp/utils'

export interface LiveVisitorsResponse {
  configured: boolean
  tenant_keys: string[]
  window_minutes: number
  active_visitors: number
  pageviews: number
  devices: { desktop: number; mobile: number; tablet: number }
  top_pages: { host: string; path: string; visitors: number }[]
  trend: { minute: string; visitors: number }[]
}

export interface RealtimeOverviewResponse {
  configured: boolean
  tenant_keys: string[]
  window_minutes: number
  active_visitors: number
  totals: { visitors: number; pageviews: number }
  devices: { desktop: number; mobile: number; tablet: number }
  series: { minute: string; visitors: number; pageviews: number }[]
  top_pages: { page: string; visitors: number; pageviews: number; product_name: string; product_image: string; avatars: string[]; anon_count: number }[]
  referrers: { source: string; via: string; visitors: number; avatars: string[]; anon_count: number }[]
  locations: { city: string; country: string; visitors: number }[]
  stores: { host: string; visitors: number; pageviews: number }[]
}

export const LIVE_VISITORS_POLL_MS = 30_000

export function useLiveVisitors() {
  return useQuery({
    queryKey: ['live-visitors'],
    queryFn: () =>
      frappeMethod<LiveVisitorsResponse>(
        'zaviago_backend.api.live_visitors.get_live_visitors',
        { minutes: 5 },
      ),
    refetchInterval: LIVE_VISITORS_POLL_MS,
    placeholderData: keepPreviousData,
    staleTime: LIVE_VISITORS_POLL_MS,
  })
}

export interface ActiveNowResponse {
  configured: boolean
  active_visitors: number
  pageviews_5m: number
}

/** GA-style fast lane: just the live counter, every 10s (backend caches 8s). */
export function useActiveNow() {
  return useQuery({
    queryKey: ['live-visitors-now'],
    queryFn: () =>
      frappeMethod<ActiveNowResponse>(
        'zaviago_backend.api.live_visitors.get_active_now',
      ),
    refetchInterval: 10_000,
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  })
}

export interface LiveFeedEvent {
  ts: string
  kind:
    | 'order'
    | 'checkout'
    | 'payment'
    | 'shipping'
    | 'coupon'
    | 'quote'
    | 'add_to_cart'
    | 'view_product'
    | 'cart'
    | 'favorites'
    | 'account'
  name: string
  avatar: string
  city: string
  host: string
  path: string
  product: string
  product_image: string
  amount: number
  detail: string
  items: { name: string; qty: number; image: string }[]
  device: string
  browser: string
  os: string
  source: string
  visitor_tag: string
  ip: string
  os_version: string
  province: string
  country_code: string
  country: string
  person_key: string
  pay_status: '' | 'paid' | 'partial' | 'unpaid'
  pages_today: number
  view_count: number
  cust_orders: number
  cust_spend: number
  open_cart_value: number
  customer_id: string
  line_uid: string
}

export interface LiveFeedResponse {
  configured: boolean
  events: LiveFeedEvent[]
}

/** WooCommerce-style live activity stream — newest first, every 10s.
 * Default: 50 events / 30 min (home + realtime page). The activity page
 * passes { minutes: 1440, limit: 100 } for "last 100 events". */
export function useLiveFeed(opts?: { minutes?: number; limit?: number; kinds?: string[] }) {
  const minutes = opts?.minutes ?? 30
  const limit = opts?.limit ?? 50
  const kinds = (opts?.kinds ?? []).join(',')
  return useQuery({
    queryKey: ['live-feed', minutes, limit, kinds],
    queryFn: () =>
      frappeMethod<LiveFeedResponse>('zaviago_backend.api.live_feed.get_live_feed', {
        minutes,
        limit,
        ...(kinds ? { kinds } : {}),
      }),
    refetchInterval: 10_000,
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  })
}

export function useRealtimeOverview() {
  return useQuery({
    queryKey: ['realtime-overview'],
    queryFn: () =>
      frappeMethod<RealtimeOverviewResponse>(
        'zaviago_backend.api.live_visitors.get_realtime_overview',
        { minutes: 30 },
      ),
    refetchInterval: LIVE_VISITORS_POLL_MS,
    placeholderData: keepPreviousData,
    staleTime: LIVE_VISITORS_POLL_MS,
  })
}

export interface SparkPair {
  cur: number[]
  prev: number[]
}

export interface DailyStatsResponse {
  configured: boolean
  spark?: {
    visitors: SparkPair
    pageviews: SparkPair
    conversion: SparkPair
    cart_value: SparkPair
    sales_value: SparkPair
    aov: SparkPair
    abandon: SparkPair
  }
  visitors: number
  pageviews: number
  orders: number
  buyers: number
  conversion_pct: number
  cart_count: number
  cart_value: number
  sales_count: number
  sales_value: number
  aov: number
  abandon_pct: number
  deltas: {
    visitors: number | null
    pageviews: number | null
    conversion: number | null
    cart_value: number | null
    sales_value: number | null
    aov: number | null
    abandon: number | null
  }
}

/** 24h KPI strip — visitors, pageviews, conversion, cart value, sales. */
export function useDailyStats() {
  return useQuery({
    queryKey: ['live-daily-stats'],
    queryFn: () =>
      frappeMethod<DailyStatsResponse>('zaviago_backend.api.live_daily.get_daily_stats'),
    refetchInterval: 20_000,
    placeholderData: keepPreviousData,
    staleTime: 20_000,
  })
}

export interface OpenCart {
  name: string
  customer: string
  customer_name: string
  value: number
  modified: string
  phone: string
  items: { name: string; qty: number; image: string }[]
  /** LINE profile picture from the shopper's identity (empty for guests) */
  avatar: string
  /** Past confirmed orders — 0 for guests (their shared record would lie) */
  past_orders: number
}

export interface OpenCartsResponse {
  configured: boolean
  carts: OpenCart[]
  total_value: number
  count: number
}

export interface SocialPost {
  id: string
  message: string
  created_time: string
  permalink: string
  picture: string
  /** photo | album | video | link (from the post's first attachment) */
  media_type: string
  comments: number
  reactions: number
  shares: number
  /** Plays for video posts (Video node views — no read_insights needed) */
  video_views: number
  /** Signed fbcdn MP4 for video posts — refreshed by the 15-min collector */
  video_url: string
  /** Reaction breakdown by type */
  rx: { like: number; love: number; haha: number; wow: number; sad: number; angry: number }
  /** True when the page token carries read_insights (post metrics below valid) */
  has_insights: boolean
  clicks: number
  avg_watch_ms: number
  watch_time_ms: number
  /** 1-minute views (post_video_views_60s_excludes_shorter) */
  views_1m: number
  /** Audience retention: % still watching per time bucket (video posts) */
  retention: number[]
  /** Watch time (ms) by age/gender bucket, e.g. {"F.25-34": 11014535} */
  vid_demo: Record<string, number>
  top_comments: { name: string; message: string; likes: number; created_time: string }[]
}

export interface SocialInsightPoint {
  d: string
  v: number
}

/** Daily series straight from Meta page insights (56-day window) */
export interface SocialPageInsights {
  page_post_engagements: SocialInsightPoint[]
  page_video_views: SocialInsightPoint[]
  page_follows: SocialInsightPoint[]
  page_daily_follows_unique: SocialInsightPoint[]
  /** Page profile visits per day */
  page_views_total: SocialInsightPoint[]
}

export interface SocialIgMedia {
  id: string
  caption: string
  media_type: string
  thumbnail: string
  permalink: string
  likes: number
  comments: number
  timestamp: string
}

/** Linked Instagram business account (basic stats, no extra scope needed) */
export interface SocialIg {
  username: string
  followers: number
  media_count: number
  picture: string
  media: SocialIgMedia[]
}

export interface SocialPage {
  page_id: string
  name: string
  followers: number
  link: string
  picture: string
  posts: SocialPost[]
  insights: SocialPageInsights | null
  ig: SocialIg | null
  /** Chatwoot conversations opened per day on this page's FB inbox */
  chat_daily: SocialInsightPoint[]
}

export interface SocialHistoryPoint {
  date: string
  page_id: string
  followers: number
  engagement: number
}

export interface SocialFbVisitor {
  name: string
  avatar: string
  last_seen: string
  ordered: boolean
  customer_id: string
}

export interface SocialFbStats {
  window_days: number
  visitors: number
  buyers: number
  conversion_pct: number
  /** Baht revenue from FB-attributed orders in the window */
  revenue: number
  latest: SocialFbVisitor[]
}

export interface SocialStatsResponse {
  configured: boolean
  range: { since: string; until: string } | null
  computing: boolean
  pages: SocialPage[]
  fetched_at: string | null
  history: SocialHistoryPoint[]
  fb: SocialFbStats | null
  /** Daily unique storefront visitors (28d) — engagement chart overlay */
  store_daily: SocialInsightPoint[]
  /** Social (FB+IG) → storefront funnel, 28d, exact aggregates */
  social_funnel: {
    window_days: number
    visitors: number
    buyers: number
    orders: number
    revenue: number
    members: number
    member_buyers: number
    member_nonspenders: number
    pageviews: number
    /** Visited on 2+ distinct days in the window (same browser / logged-in) */
    returning: number
  } | null
  /** Unique Chatwoot contacts on FB/IG inboxes, 28d */
  chat_users: number
  /** Ad campaigns detected from first-party traffic (no ad integration) */
  campaigns: {
    window_days: number
    campaigns: {
      name: string
      platform: string
      paid: boolean
      people: number
      views: number
      sessions: number
      bounce_pct: number
      returning: number
      buyers: number
      cvr_pct: number
      atc_rate: number
      carts_added: number
      abandoned: number
      abandon_pct: number
      members: number
      new_members: number
      orders: number
      revenue: number
      revenue_exact: boolean
      aov: number
      new_customers: number
      trend_pct: number | null
      retargeting: boolean
    }[]
    paid_clicks: { meta: number; google: number; tiktok: number }
    retarget_people: number
  } | null
  /** Pixel config health + retargetable audiences + recoverable carts */
  pipeline: {
    config: {
      meta_pixel: boolean
      meta_capi: boolean
      ga4: boolean
      google_ads: boolean
      tracking_enabled: boolean
    }
    segments: {
      hot_checkout: number
      viewers_no_buy: number
      buyers_90d: number
      customers_phone: number
    }
    line_oa: { followers: number; targeted_reach: number; blocks: number; connected: boolean }
    audiences: {
      cookie_pool: number
      line_members: number
      events_24h: number
      events_hourly: { h: string; v: number }[]
      events_by_type: { event: string; v: number; people: number }[]
      /** Top clicked button texts, 24h ($autocapture drill-down) */
      clicks_by_button: { label: string; v: number; people: number }[]
      /** Top viewed pages, 24h ($pageview drill-down) */
      views_by_page: { label: string; v: number; people: number }[]
      /** New people entering the retarget pool per day (28d) */
      cookie_daily: { d: string; v: number }[]
      /** New LINE members per day (28d) */
      line_daily: { d: string; v: number }[]
    }
    carts: {
      count: number
      value: number
      contactable: number
      contactable_value: number
      fresh_count: number
      fresh_value: number
      daily: { d: string; count: number; value: number }[]
      top_items: { name: string; carts: number; value: number; image: string }[]
      window_days: number
    }
    retarget_days: number
  } | null
}

/** Facebook page + post engagement, synced from Chatwoot's Meta connections. */
export interface SocialRange {
  since: string
  until: string
}

export function useSocialStats(range?: SocialRange | null) {
  return useQuery({
    queryKey: ['social-stats', range ?? 'default'],
    queryFn: () =>
      frappeMethod<SocialStatsResponse>(
        'zaviago_backend.api.social_stats.get_social_stats',
        range ? { since: range.since, until: range.until } : undefined,
      ),
    // Custom ranges compute in the background server-side — poll fast until
    // `computing` clears, then settle to the normal 5-min refresh.
    refetchInterval: (query) => (query.state.data?.computing ? 4_000 : 5 * 60_000),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
  })
}

/** The follow-up list behind the "ในตะกร้า" KPI. */
export function useOpenCarts() {
  return useQuery({
    queryKey: ['open-carts'],
    queryFn: () =>
      frappeMethod<OpenCartsResponse>('zaviago_backend.api.live_daily.get_open_carts'),
    refetchInterval: 60_000,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  })
}
